import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { useAuth } from '../../store/AuthContext';
import { Brain, Save, Loader2, AlertCircle, Info, RefreshCw } from 'lucide-react';

const PromptEditorPage = () => {
  const { token } = useAuth();
  const [prompts, setPrompts] = useState({
    prompt_market_intel_system: '',
    prompt_market_intel_user: '',
    formula_overpass_query: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState(null);

  const defaultPrompts = {
    prompt_market_intel_system: 'Eres un experto en análisis de mercado retail y selección de sitios. Respondes únicamente en formato JSON válido y contextualizado geográficamente.',
    prompt_market_intel_user: `Eres un experto en análisis de mercado y localización de negocios. 

Dado un negocio de la marca "{{brand}}" en el mercado de "{{market}}" y operando principalmente en el país "{{country}}", necesito que me proporciones:

1. **competence** (competidores): Lista de nombres de marcas reales que son competencia directa EXCLUSIVAMENTE en el país "{{country}}" para ese mercado. 
   IMPORTANTE: NO incluyas bajo ningún concepto a "{{brand}}" dentro de esta lista. Solo competidores externos.

2. **engines** (motores/complementarios): Lista de NOMBRES DE MARCAS REALES (no sectores, no categorías genéricas) de negocios que son complementarios y que atraerían tráfico beneficioso si estuvieran físicamente cerca, basándote ÚNICAMENTE en la cultura de consumo y presencia comercial de "{{country}}".
   EJEMPLO PARA ENGINES: Si el mercado es "Cafeterías" en España, no pongas "Supermercados", pon marcas reales con presencia física en España como "Mercadona", "Carrefour" o "Lidl".

Responde ÚNICAMENTE con un JSON válido (sin markdown, sin explicaciones) con esta estructura exacta:
{
  "competence": ["Marca Competidora 1", "Marca Competidora 2", ...],
  "engines": ["Marca Motor 1", "Marca Motor 2", ...]
}

Incluye al menos 8-12 marcas reales en cada array que REALMENTE EXISTAN en {{country}}.`,
    formula_overpass_query: `[out:json][timeout:90];
{{areaFilter}}
(
{{selection}}
);
out center;`
  };

  const fetchPrompts = async () => {
    try {
      const resp = await axios.get(`${getApiUrl()}/setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const settings = resp.data.data;
      const newPrompts = { ...prompts };
      
      const systemPrompt = settings.find(s => s.key === 'prompt_market_intel_system');
      if (systemPrompt) newPrompts.prompt_market_intel_system = systemPrompt.value;
      else newPrompts.prompt_market_intel_system = defaultPrompts.prompt_market_intel_system;

      const userPrompt = settings.find(s => s.key === 'prompt_market_intel_user');
      if (userPrompt) newPrompts.prompt_market_intel_user = userPrompt.value;
      else newPrompts.prompt_market_intel_user = defaultPrompts.prompt_market_intel_user;

      const overpassQuery = settings.find(s => s.key === 'formula_overpass_query');
      if (overpassQuery) newPrompts.formula_overpass_query = overpassQuery.value;
      else newPrompts.formula_overpass_query = defaultPrompts.formula_overpass_query;

      setPrompts(newPrompts);
    } catch (err) {
      console.error('Failed to fetch prompts', err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (token) fetchPrompts();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await Promise.all(
        Object.entries(prompts).map(([key, value]) => 
          axios.post(`${getApiUrl()}/setup`, { key, value }, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      setMessage({ type: 'success', text: 'AI PROMPTS RE-CONFIGURED ACCORDING TO MISSION PARAMETERS.' });
    } catch (err) {
      setMessage({ type: 'error', text: 'FAILED TO UPDATE MISSION PROMPTS. CHECK CONFIGURED LOGS.' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (key) => {
    setPrompts(prev => ({ ...prev, [key]: defaultPrompts[key] }));
  };

  if (fetching) return <div style={{ color: '#0B3D91', padding: '20px' }}>ACCESSING NEURAL CORE...</div>;

  return (
    <div style={{ maxWidth: '1000px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px' }}>
        <Brain size={28} color="#00CEC9" />
        <h1 style={{ margin: 0, letterSpacing: '2px', textTransform: 'uppercase' }}>AI Behavioral Prompts</h1>
      </div>

      <div style={{ 
        background: 'rgba(5, 7, 10, 0.9)', 
        border: '1px solid #0B3D91', 
        padding: '30px',
        borderRadius: '4px'
      }}>
        <form onSubmit={handleSubmit}>
          
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ color: '#00CEC9', fontSize: '0.8rem', fontWeight: 'bold' }}>
                INTEL SYSTEM PROMPT (IDENTITY)
              </label>
              <button 
                type="button" 
                onClick={() => handleReset('prompt_market_intel_system')}
                style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '0.6rem', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <RefreshCw size={10} /> RESET TO DEFAULT
              </button>
            </div>
            <textarea
              className="nasa-input"
              style={{ minHeight: '80px', fontFamily: 'monospace', fontSize: '0.8rem' }}
              value={prompts.prompt_market_intel_system}
              onChange={(e) => setPrompts({ ...prompts, prompt_market_intel_system: e.target.value })}
              placeholder="System instructions..."
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ color: '#00CEC9', fontSize: '0.8rem', fontWeight: 'bold' }}>
                MARKET ANALYSIS USER PROMPT (TEMPLATE)
              </label>
              <button 
                type="button" 
                onClick={() => handleReset('prompt_market_intel_user')}
                style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '0.6rem', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <RefreshCw size={10} /> RESET TO DEFAULT
              </button>
            </div>
            <div style={{ marginBottom: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.6rem', color: '#E27B58', background: 'rgba(226, 123, 88, 0.1)', padding: '2px 6px', borderRadius: '2px' }}>Variable: {"{{brand}}"}</span>
              <span style={{ fontSize: '0.6rem', color: '#E27B58', background: 'rgba(226, 123, 88, 0.1)', padding: '2px 6px', borderRadius: '2px' }}>Variable: {"{{market}}"}</span>
              <span style={{ fontSize: '0.6rem', color: '#E27B58', background: 'rgba(226, 123, 88, 0.1)', padding: '2px 6px', borderRadius: '2px' }}>Variable: {"{{country}}"}</span>
            </div>
            <textarea
              className="nasa-input"
              style={{ minHeight: '350px', fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: '1.4' }}
              value={prompts.prompt_market_intel_user}
              onChange={(e) => setPrompts({ ...prompts, prompt_market_intel_user: e.target.value })}
              placeholder="AI Mission instructions..."
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ color: '#E27B58', fontSize: '0.8rem', fontWeight: 'bold' }}>
                TURBO SEARCH FORMULA (OVERPASS QL)
              </label>
              <button 
                type="button" 
                onClick={() => handleReset('formula_overpass_query')}
                style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '0.6rem', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <RefreshCw size={10} /> RESET TO DEFAULT
              </button>
            </div>
            <div style={{ marginBottom: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.6rem', color: '#00CEC9', background: 'rgba(0, 206, 201, 0.1)', padding: '2px 6px', borderRadius: '2px' }}>Variable: {"{{selection}}"} (Dynamic from Checkboxes)</span>
              <span style={{ fontSize: '0.6rem', color: '#00CEC9', background: 'rgba(0, 206, 201, 0.1)', padding: '2px 6px', borderRadius: '2px' }}>Variable: {"{{areaFilter}}"}</span>
            </div>
            <textarea
              className="nasa-input"
              style={{ minHeight: '180px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#E27B58' }}
              value={prompts.formula_overpass_query}
              onChange={(e) => setPrompts({ ...prompts, formula_overpass_query: e.target.value })}
              placeholder="Overpass QL Template..."
            />
            <p style={{ fontSize: '0.6rem', color: '#555', marginTop: '5px' }}>
              This query template is used by the Global Intel Fleet to fetch physical locations from OpenStreetMap.
            </p>
          </div>

          <div style={{ 
            backgroundColor: 'rgba(0, 206, 201, 0.05)', 
            border: '1px solid rgba(0, 206, 201, 0.3)', 
            padding: '15px', 
            marginBottom: '30px',
            display: 'flex',
            gap: '15px',
            alignItems: 'flex-start'
          }}>
            <Info size={20} color="#00CEC9" style={{ flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#00CEC9', lineHeight: '1.4' }}>
              <strong>AI CALIBRATION:</strong> Modifying these prompts changes how the Global Intel Fleet identifies competition and engines. Ensure the output format remains valid JSON as required by the mission telemetry parser.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="nasa-button"
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            {loading ? 'SYNCHRONIZING NEURAL ARCHIVE...' : 'SAVE PROMPT CONFIGURATION'}
          </button>
        </form>

        {message && (
          <div style={{
            marginTop: '20px',
            padding: '12px',
            fontSize: '0.8rem',
            backgroundColor: message.type === 'success' ? 'rgba(0, 184, 148, 0.1)' : 'rgba(214, 48, 49, 0.1)',
            color: message.type === 'success' ? '#00B894' : '#D63031',
            border: `1px solid ${message.type === 'success' ? '#00B894' : '#D63031'}`,
            textAlign: 'center'
          }}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptEditorPage;
